import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { client } from '../context/client';
import Layout from '../components/layout/Layout';
import Navbar from '../components/layout/header/Navbar';
import Footer from '../components/layout/footer/Footer';
import { Box, Typography, Card, CardMedia, CardContent, CircularProgress } from '@mui/material';
import ReactMarkdown from 'react-markdown';

function slugify(text) {
  return text
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-zA-Z0-9α-ωΑ-Ω ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

export default function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const entries = await client.getEntries({ content_type: 'news' });
        if (entries.items && entries.items.length > 0) {
          const found = entries.items.find(item => slugify(item.fields.title) === slug);
          if (found) {
            setArticle(found);
          } else {
            setError('Δεν βρέθηκε το άρθρο.');
          }
        } else {
          setError('Δεν βρέθηκε το άρθρο.');
        }
      } catch (err) {
        setError('Δεν βρέθηκε το άρθρο.');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <Navbar darkBg />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
        <Footer />
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <Navbar darkBg />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography color="error">{error || 'Σφάλμα φόρτωσης.'}</Typography>
        </Box>
        <Footer />
      </Layout>
    );
  }

  const { title, description, image, date } = article.fields;
  const imageUrl = image?.fields?.file?.url ? image.fields.file.url : '';

  return (
    <Layout>
      <Navbar darkBg />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '60vh', pt: 12, pb: 4 }}>
        <Card sx={{ maxWidth: 900, width: '100%', borderRadius: 4, boxShadow: 3, p: 2 }}>
          {imageUrl && (
            <CardMedia
              component="img"
              image={imageUrl}
              alt={title}
              sx={{ maxHeight: 350, objectFit: 'cover', borderRadius: 2, mb: 2 }}
            />
          )}
          <CardContent>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#166aa3', mb: 2 }}>
              {title}
            </Typography>

            {date && (
              <Typography variant="subtitle2" sx={{ color: '#313e5b', mb: 2 }}>
                {new Date(date).toLocaleDateString()}
              </Typography>
            )}

            <Box
              sx={{
                mt: 2,
                lineHeight: 1.8,
                fontSize: '1.05rem',
                color: '#313e5b',
                '& p': { mb: 2 },
                '& h1, & h2, & h3': {
                  color: '#166aa3',
                  mt: 3,
                  mb: 1.5,
                  fontWeight: 700,
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 2,
                },
                '& li': {
                  mb: 0.5,
                },
                '& a': {
                  color: '#166aa3',
                  textDecoration: 'underline',
                },
                '& strong': {
                  fontWeight: 700,
                },
                '& em': {
                  fontStyle: 'italic',
                },
              }}
            >
              <ReactMarkdown>{description || ''}</ReactMarkdown>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Footer />
    </Layout>
  );
}
